/**
 * World Cup Prediction Popup v2 — Production
 *
 * Features:
 *  - Logged-in customers only (WCP_CUSTOMER_EMAIL set by Liquid)
 *  - Real API: https://livgood-pt.herokuapp.com/api/v2/world-cup-predictions
 *  - Pick prediction: Home wins / Draw / Away wins
 *  - Lock time = match date + time (Beirut UTC+3) — live countdown per card
 *  - Tabs: Predictions | Upcoming | Past
 *  - Trophy milestones progress bar with claim action
 *  - Shows once per day per customer (localStorage daily key)
 */

(function () {
  'use strict';

  const API = 'https://livgood-pt.herokuapp.com/api/v2/world-cup-predictions';

  /* ── Time helpers (Beirut = UTC+3) ── */
  function lockTime(match) {
    return new Date(`${match.date}T${match.time}:00+03:00`);
  }
  function isLocked(match) {
    return Date.now() >= lockTime(match).getTime();
  }
  function countdown(match) {
    const diff = lockTime(match).getTime() - Date.now();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
  function pad(n) { return String(n).padStart(2, '0'); }

  function fmtDate(dateStr, timeStr) {
    const d = new Date(`${dateStr}T${timeStr}:00+03:00`);
    return d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })
      + ' · ' + timeStr;
  }

  /* ── API calls ── */
  async function apiMatches() {
    try {
      const r = await fetch(`${API}/matches`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      console.log('[WCP] /matches raw response:', j);
      /* handle both { matches: [...] } and a bare array */
      const matches = Array.isArray(j) ? j : (j.matches || j.data || []);
      console.log('[WCP] matches parsed:', matches.length, 'items');
      return matches;
    } catch (err) {
      console.error('[WCP] /matches failed:', err.message);
      return [];
    }
  }

  async function apiSubmit(email, predictions) {
    const r = await fetch(`${API}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, predictions }),
    });
    return r.json();
  }

  async function apiStats(email) {
    const r = await fetch(`${API}/stats/${encodeURIComponent(email)}`);
    return r.json();
  }

  async function apiPredictions(email) {
    const r = await fetch(`${API}/predictions/${encodeURIComponent(email)}`);
    return r.json();
  }

  /* ── Local storage ── */
  const PRED_KEY = 'wcp_predictions_v2';

  function loadLocalPreds() {
    try { return JSON.parse(localStorage.getItem(PRED_KEY) || '{}'); } catch { return {}; }
  }
  function saveLocalPreds(obj) {
    localStorage.setItem(PRED_KEY, JSON.stringify(obj));
  }
  function getEmail() {
    return window.WCP_CUSTOMER_EMAIL || null;
  }

  /* ── Component ── */
  class WorldCupPrediction extends HTMLElement {

    connectedCallback() {
      /* state */
      this._matches    = [];
      this._stats      = null;
      this._predictions = null;  // data from /predictions/:email
      this._tab        = 'predictions';
      this._pending    = {};   // pick string per match_id, confirmed but not yet submitted
      this._submitted  = loadLocalPreds();
      this._email      = getEmail();
      this._timer      = null;

      /* DOM refs */
      this._overlay    = this.querySelector('.wcp-overlay');
      this._statsEl    = this.querySelector('.wcp-stats-section');
      this._tabBody    = this.querySelector('.wcp-tab-body');
      this._submitArea = this.querySelector('.wcp-submit-area');
      this._htwOverlay = this.querySelector('.wcp-htw-overlay');

      /* logged-in users only */
      if (!window.WCP_CUSTOMER_EMAIL) {
        console.log('[WCP] blocked: not logged in');
        return;
      }

      console.log('[WCP] loading matches…');

      this._bindStatic();
      this._load();
    }

    disconnectedCallback() {
      clearInterval(this._timer);
    }

    /* ── Load data & show ── */
    async _load() {
      try {
        this._matches = await apiMatches();
        if (!this._matches.length) return;

        /* fetch stats early — merges API-submitted picks so user can't re-predict */
        this._fetchStats();
        this._fetchPredictions();

        this._renderStats();
        this._renderTab();
        this._renderSubmitArea();
        this._startTimer();
        setTimeout(() => this._open(), 1200);
      } catch (err) {
        console.error('[WCP] Load error:', err);
      }
    }

    async _fetchStats() {
      try {
        const res = await apiStats(this._email);
        if (res.ok && res.data) {
          this._stats = res.data;
          /* sync server-side submitted picks into local state so predictions are locked */
          if (Array.isArray(res.data.predictions)) {
            res.data.predictions.forEach(p => {
              if (!this._submitted[p.match_id]) {
                this._submitted[p.match_id] = { pick: p.pick };
              }
            });
            saveLocalPreds(this._submitted);
          }
          this._renderStats();
          this._renderTab();
        }
      } catch (err) {
        console.error('[WCP] Stats error:', err);
      }
    }

    async _fetchPredictions() {
      try {
        const res = await apiPredictions(this._email);
        if (res.ok && res.data) {
          this._predictions = res.data;
          if (this._tab === 'past') this._renderTab();
        }
      } catch (err) {
        console.error('[WCP] Predictions error:', err);
      }
    }

    /* ── Open / Close ── */
    _open() {
      this._overlay.style.display = '';
      requestAnimationFrame(() => {
        this._overlay.classList.add('wcp-active');
        document.body.style.overflow = 'hidden';
      });
    }
    _close() {
      this._overlay.classList.remove('wcp-active');
      document.body.style.overflow = '';
      clearInterval(this._timer);
      setTimeout(() => { this._overlay.style.display = 'none'; }, 400);
    }

    /* ── Render: Stats / Milestones ── */
    _renderStats() {
      if (!this._statsEl) return;

      if (!this._stats) {
        /* loading skeleton */
        this._statsEl.innerHTML = `
          <div class="wcp-stats-card">
            <div class="wcp-stats-top">
              <span class="wcp-stats-label">Loading your progress…</span>
              <span class="wcp-trophy-pill">— 🏆</span>
            </div>
            <div class="wcp-milestone-scroll">
              <div class="wcp-milestone-track">
                ${[0,1,2,3,4,5].map(() => `
                  <div class="wcp-ms-item">
                    <div class="wcp-ms-dot wcp-skel" style="height:18px"></div>
                    <div class="wcp-ms-label wcp-skel" style="width:56px;height:9px;margin-top:8px"></div>
                  </div>`).join('')}
              </div>
            </div>
          </div>`;
        return;
      }

      const s = this._stats;
      const label = s.trophiesToNextMilestone > 0
        ? `${s.trophiesToNextMilestone} more trophies to next reward`
        : 'All milestones unlocked! 🎉';

      /* progress % toward next milestone */
      const nextMs = s.milestones.find(m => !m.unlocked);
      const prevThreshold = nextMs
        ? (s.milestones[s.milestones.indexOf(nextMs) - 1]?.trophiesRequired || 0)
        : s.milestones.at(-1)?.trophiesRequired || 1;
      const progressPct = nextMs
        ? Math.min(100, Math.round(((s.trophies - prevThreshold) / (nextMs.trophiesRequired - prevThreshold)) * 100))
        : 100;

      const msHtml = s.milestones.map((ms, i) => {
        const prevAllDone = s.milestones.slice(0, i).every(m => m.unlocked);
        let cls = '';

        if (ms.unlocked && ms.claimed) {
          cls = 'ms-done ms-claimed';
        } else if (ms.unlocked) {
          cls = 'ms-done';
        } else if (prevAllDone) {
          cls = 'ms-current';
        }
        
        return `
          <div class="wcp-ms-item ${cls}" data-ms="${ms.index}">
            <div class="wcp-ms-dot"></div>
            <div class="wcp-ms-trophies">${ms.trophiesRequired} 🏆</div>
            <div class="wcp-ms-label">${ms.label}</div>
          </div>`;
      }).join('');

      this._statsEl.innerHTML = `
        <div class="wcp-stats-card">
          <div class="wcp-stats-top">
            <span class="wcp-stats-label">${label}</span>
            <span class="wcp-trophy-pill">${s.trophies} 🏆</span>
          </div>
          <div class="wcp-progress-bar">
            <div class="wcp-progress-fill" style="width:${progressPct}%"></div>
          </div>
          <div class="wcp-milestone-scroll">
            <div class="wcp-milestone-track">${msHtml}</div>
          </div>
        </div>`;
    }

    /* ── Render: Tabs ── */
    _renderTab() {
      this.querySelectorAll('.wcp-tab').forEach(t =>
        t.classList.toggle('wcp-tab--active', t.dataset.tab === this._tab)
      );
      if (this._tab === 'predictions') this._tabPredictions();
      else if (this._tab === 'upcoming') this._tabUpcoming();
      else this._tabPast();
    }

    _tabPredictions() {
      const open = this._matches.filter(m => !isLocked(m) && !this._submitted[m.match_id]);
      if (!open.length) {
        const hasAny = this._matches.some(m => !isLocked(m));
        this._tabBody.innerHTML = `
          <div class="wcp-empty">
            <span class="wcp-empty-icon">⚽</span>
            ${hasAny
              ? "You've predicted all open matches! Check <b>Upcoming</b> to review your picks."
              : "No open matches right now. Check back soon!"}
          </div>`;
        return;
      }
      let html = '<p style="font-size:12px;color:#6b7280;font-weight:600;margin-bottom:10px;">Choose the winner ⬇️</p>';
      open.forEach(m => { html += this._cardHtml(m, 'predict'); });
      this._tabBody.innerHTML = html;
      this._bindCards(this._tabBody);
    }

    _tabUpcoming() {
      const list = this._matches.filter(m => !m.result);
      if (!list.length) {
        this._tabBody.innerHTML = `<div class="wcp-empty"><span class="wcp-empty-icon">📅</span>No upcoming matches.</div>`;
        return;
      }
      let html = '';
      list.forEach(m => {
        const mode = isLocked(m)           ? 'locked'
                   : this._submitted[m.match_id] ? 'submitted'
                   : 'predict';
        html += this._cardHtml(m, mode);
      });
      this._tabBody.innerHTML = html;
      this._bindCards(this._tabBody);
    }

    _tabPast() {
      /* loading state */
      if (!this._predictions) {
        this._tabBody.innerHTML = `
          <div class="wcp-empty">
            <span class="wcp-empty-icon">⏳</span>
            Loading your predictions…
          </div>`;
        return;
      }

      const { summary, predictions } = this._predictions;
      const resolved = predictions.filter(p => p.resolved);
      const pending  = predictions.filter(p => !p.resolved);

      if (!predictions.length) {
        this._tabBody.innerHTML = `
          <div class="wcp-empty">
            <span class="wcp-empty-icon">🏁</span>
            No predictions yet.
          </div>`;
        return;
      }

      /* summary bar */
      const summaryHtml = `
        <div class="wcp-pred-summary">
          <span class="wcp-pred-stat wcp-pred-stat--correct">✓ ${summary.correct} Correct</span>
          <span class="wcp-pred-stat wcp-pred-stat--wrong">✗ ${summary.incorrect} Wrong</span>
          <span class="wcp-pred-stat wcp-pred-stat--pending">⏳ ${summary.pending} Pending</span>
        </div>`;

      const pickLabel = (pick, home, away) => {
        if (pick === 'home') return `${home} wins`;
        if (pick === 'away') return `${away} wins`;
        return 'Draw';
      };

      const cards = predictions.map(p => {
        let statusCls = '', statusBadge = '';
        if (!p.resolved) {
          statusCls  = 'wcp-pred--pending';
          statusBadge = `<span class="wcp-pred-badge wcp-pred-badge--pending">⏳ Pending</span>`;
        } else if (p.is_correct) {
          statusCls  = 'wcp-pred--correct';
          statusBadge = `<span class="wcp-pred-badge wcp-pred-badge--correct">✓ Correct</span>`;
        } else {
          statusCls  = 'wcp-pred--wrong';
          statusBadge = `<span class="wcp-pred-badge wcp-pred-badge--wrong">✗ Wrong</span>`;
        }

        const resultLine = p.resolved && p.result
          ? `<span class="wcp-pred-result">Result: ${pickLabel(p.result, p.home, p.away)}</span>`
          : '';

        return `
          <div class="wcp-pred-card ${statusCls}">
            <div class="wcp-pred-meta">
              <span class="wcp-pred-date">${fmtDate(p.date, p.time)}</span>
              <span class="wcp-pred-group">${p.group}</span>
            </div>
            <div class="wcp-pred-matchup">
              <span class="wcp-pred-team">${p.home}</span>
              <span class="wcp-vs-label">VS</span>
              <span class="wcp-pred-team">${p.away}</span>
            </div>
            <div class="wcp-pred-footer">
              <span class="wcp-pred-pick">Your pick: ${pickLabel(p.pick, p.home, p.away)}</span>
              <div class="wcp-pred-right">
                ${resultLine}
                ${statusBadge}
              </div>
            </div>
          </div>`;
      }).join('');

      this._tabBody.innerHTML = summaryHtml + cards;
    }

    /* ── Match card HTML builder ── */
    _cardHtml(match, mode) {
      const id      = match.match_id;
      const pending = this._pending[id];            // string: 'home'|'away'|'draw' or undefined
      const sub     = this._submitted[id];          // { pick: 'home'|'away'|'draw' } or undefined
      const cd      = countdown(match);
      const urgent  = cd && cd.startsWith('00:0');

      /* card class */
      let cls = 'wcp-match-card';
      if (pending)              cls += ' wcp--confirmed';
      else if (mode === 'locked')    cls += ' wcp--locked';
      else if (mode === 'submitted') cls += ' wcp--submitted';

      /* helper — readable pick label */
      const pickLabel = (pick) => {
        if (pick === 'home') return `${match.home.flag} ${match.home.name} wins`;
        if (pick === 'away') return `${match.away.flag} ${match.away.name} wins`;
        return '🤝 Draw';
      };

      /* pick / result area — rendered below the teams row */
      let pickAreaHtml = '';

      if (mode === 'predict' && !pending) {
        /* 3 pick buttons */
        pickAreaHtml = `
          <div class="wcp-picks">
            <button class="wcp-pick" data-mid="${id}" data-pick="home">
              <span class="wcp-pick-flag">${match.home.flag}</span>
              <span class="wcp-pick-label">${match.home.name} wins</span>
            </button>
            <button class="wcp-pick wcp-pick--draw" data-mid="${id}" data-pick="draw">
              <span class="wcp-pick-flag">🤝</span>
              <span class="wcp-pick-label">Draw</span>
            </button>
            <button class="wcp-pick" data-mid="${id}" data-pick="away">
              <span class="wcp-pick-flag">${match.away.flag}</span>
              <span class="wcp-pick-label">${match.away.name} wins</span>
            </button>
          </div>`;
      } else if (pending) {
        pickAreaHtml = `
          <div class="wcp-pick-confirmed">
            <span class="wcp-pick-confirmed-label">✓ ${pickLabel(pending)}</span>
            <button class="wcp-edit-btn" data-mid="${id}">Edit</button>
          </div>`;
      } else if (sub && (mode === 'submitted' || mode === 'locked')) {
        pickAreaHtml = `
          <div class="wcp-status-line wcp-status-line--gray">
            ✓ Submitted: ${pickLabel(sub.pick)}
          </div>`;
      } else if (mode === 'past') {
        const resultLabel = match.result ? pickLabel(match.result) : '—';
        const myPick      = sub ? sub.pick : null;
        const correct     = myPick && match.result && myPick === match.result;

        pickAreaHtml = `
          <div class="wcp-past-result">
            <span class="wcp-past-result-label">Result: ${resultLabel}</span>
            ${myPick ? `
              <span class="wcp-my-pred wcp-my-pred--${correct ? 'correct' : 'wrong'}">
                ${correct ? '🏆 Correct!' : '✗ Your pick: ' + pickLabel(myPick)}
              </span>` : ''}
          </div>`;
      }

      /* countdown / locked label */
      let timeHtml = '';
      if (!isLocked(match) && (mode === 'predict' || mode === 'upcoming')) {
        timeHtml = `
          <div class="wcp-countdown${urgent ? ' wcp-countdown--urgent' : ''}" data-cd="${id}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Locks in ${cd || '00:00:00'}
          </div>`;
      } else if (mode === 'locked') {
        timeHtml = `<div class="wcp-locked-label">🔒 Predictions locked</div>`;
      }

      return `
        <div class="${cls}" data-mid="${id}">
          <div class="wcp-match-meta">
            <span class="wcp-match-dt">${fmtDate(match.date, match.time)}</span>
            <span class="wcp-match-group">${match.group}</span>
          </div>
          <div class="wcp-score-layout">
            <div class="wcp-team-col">
              <div class="wcp-team-badge">${match.home.flag}</div>
              <div class="wcp-team-info">
                <span class="wcp-team-name">${match.home.name}</span>
                <span class="wcp-team-code">${match.home.code}</span>
              </div>
            </div>
            <div class="wcp-score-center">
              <span class="wcp-vs-label">VS</span>
            </div>
            <div class="wcp-team-col wcp-team-col--away">
              <div class="wcp-team-badge">${match.away.flag}</div>
              <div class="wcp-team-info">
                <span class="wcp-team-name">${match.away.name}</span>
                <span class="wcp-team-code">${match.away.code}</span>
              </div>
            </div>
          </div>
          ${pickAreaHtml}
          ${timeHtml}
        </div>`;
    }

    /* ── Bind card events ── */
    _bindCards(container) {
      /* pick buttons — clicking immediately confirms the pick */
      container.querySelectorAll('.wcp-pick').forEach(btn => {
        btn.addEventListener('click', () => {
          const { mid, pick } = btn.dataset;
          this._pending[mid] = pick;  // store pick string directly
          this._renderTab();
          this._renderSubmitArea();
        });
      });

      /* edit — clears the pending pick so buttons show again */
      container.querySelectorAll('.wcp-edit-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          delete this._pending[btn.dataset.mid];
          this._renderTab();
          this._renderSubmitArea();
        });
      });
    }

    /* ── Render: Submit area ── */
    _renderSubmitArea() {
      if (!this._submitArea) return;
      const count = Object.keys(this._pending).length;
      if (!count) { this._submitArea.innerHTML = ''; return; }

      const plural = count > 1 ? 's' : '';
      this._submitArea.innerHTML = `
        <div class="wcp-submit-card">
          <p class="wcp-submit-hint">${count} prediction${plural} ready — lock them in!</p>
          <div class="wcp-submit-row">
            <button class="wcp-submit-go" id="wcp-go">
              <span class="wcp-btn-lbl">Submit ${count} prediction${plural} 🔒</span>
              <span class="wcp-spinner"></span>
            </button>
          </div>
        </div>`;

      this._submitArea.querySelector('#wcp-go')
        .addEventListener('click', () => this._doSubmit());
    }

    /* ── Submit ── */
    async _doSubmit() {
      const email = this._email;
      if (!email) return;

      const btn = this._submitArea.querySelector('#wcp-go');
      if (btn) { btn.classList.add('loading'); btn.disabled = true; }

      const predictions = Object.entries(this._pending).map(([match_id, pick]) => ({
        match_id,
        pick,
      }));

      try {
        const res = await apiSubmit(email, predictions);

        if (res.ok || (res.saved && res.saved > 0)) {
          predictions.forEach(p => {
            this._submitted[p.match_id] = { pick: p.pick };
          });
          saveLocalPreds(this._submitted);
          this._pending = {};
          this._renderSubmitArea();
          this._renderTab();
          this._fetchStats();
        } else {
          throw new Error(res.message || 'Submit failed');
        }
      } catch (err) {
        console.error('[WCP] Submit error:', err);
        if (btn) { btn.classList.remove('loading'); btn.disabled = false; }
        alert('Something went wrong. Please try again.');
      }
    }

    /* ── Static event bindings (run once) ── */
    _bindStatic() {
      /* close overlay */
      this._overlay?.addEventListener('click', e => {
        if (e.target === this._overlay) this._close();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this._overlay?.classList.contains('wcp-active')) this._close();
      });
      this.querySelector('.wcp-close')?.addEventListener('click', () => this._close());

      /* tabs */
      this.querySelectorAll('.wcp-tab').forEach(tab => {
        tab.addEventListener('click', () => {
          this._tab = tab.dataset.tab;
          this._renderTab();
        });
      });

      /* how to win */
      this.querySelector('.wcp-htw-trigger')?.addEventListener('click', () => {
        if (this._htwOverlay) this._htwOverlay.style.display = '';
      });
      this.querySelector('.wcp-htw-close')?.addEventListener('click', () => {
        if (this._htwOverlay) this._htwOverlay.style.display = 'none';
      });
      this._htwOverlay?.addEventListener('click', e => {
        if (e.target === this._htwOverlay) this._htwOverlay.style.display = 'none';
      });
    }

    /* ── Countdown timer (runs every second) ── */
    _startTimer() {
      clearInterval(this._timer);
      this._timer = setInterval(() => {
        if (!this._tabBody) return;
        let needsRefresh = false;
        this._tabBody.querySelectorAll('[data-cd]').forEach(el => {
          const mid   = el.dataset.cd;
          const match = this._matches.find(m => m.match_id === mid);
          if (!match) return;
          const cd = countdown(match);
          if (!cd) { needsRefresh = true; return; }
          const urgent = cd.startsWith('00:0');
          el.className = `wcp-countdown${urgent ? ' wcp-countdown--urgent' : ''}`;
          el.innerHTML = `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Locks in ${cd}`;
        });
        if (needsRefresh) {
          this._renderTab();
          this._renderSubmitArea();
        }
      }, 1000);
    }
  }

  if (!customElements.get('worldcup-prediction')) {
    customElements.define('worldcup-prediction', WorldCupPrediction);
  }
})();
